/**
 * Server-Authoritative Listing Lifecycle & Renewal Service
 * Cameroon Land Market
 * 
 * Handles:
 * 1. Derivation and validation of cityKey and regionKey against central registry
 * 2. Ownership verification and atomic listing renewal
 * 3. Idempotent expiration detection and state transition
 * 4. Atomic city and region statistics increments and decrements (never negative)
 */

import {
  doc,
  getDoc,
  updateDoc,
  setDoc,
  serverTimestamp,
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from './firestoreDb';
import { app } from './firebase';
import { httpsCallable, getFunctions } from 'firebase/functions';

const functions = getFunctions(app, 'us-central1');
import { Property, CityStats, RegionStats, ListingVerificationStatus } from '../types';
import { LISTING_CONFIG, calculateExpiryDate, isListingExpired } from '../config/listingConfig';
import { ALL_CAMEROON_TOWNS, CAMEROON_REGIONS } from '../data/cameroonCities';
import { slugify, getTrustedCity } from './routes';

/**
 * Invokes the trusted Cloud Function to publish a listing, setting
 * status: 'published', isPublic: true, publishedAt: serverTimestamp(), expiresAt.
 */
export async function publishListingBackend(propertyId: string): Promise<{
  success: boolean;
  propertyId: string;
  status: string;
  isPublic: boolean;
  expiresAt: string;
}> {
  const callable = httpsCallable<{ propertyId: string }, any>(functions, 'publishListing');
  const result = await callable({ propertyId });
  return result.data;
}

/**
 * Invokes the trusted administrator Cloud Function to update a property's
 * official cadastral verification status ('verified', 'pending', 'rejected', 'unverified').
 */
export async function adminSetVerificationStatusBackend(
  propertyId: string,
  verificationStatus: ListingVerificationStatus,
  titreFoncier?: string,
  verificationDetails?: any
): Promise<{
  success: boolean;
  propertyId: string;
  verificationStatus: ListingVerificationStatus;
}> {
  const callable = httpsCallable<
    {
      propertyId: string;
      verificationStatus: string;
      titreFoncier?: string;
      verificationDetails?: any;
    },
    {
      success: boolean;
      propertyId: string;
      verificationStatus: ListingVerificationStatus;
    }
  >(
    functions,
    'adminSetVerificationStatus'
  );
  const result = await callable({
    propertyId,
    verificationStatus,
    titreFoncier,
    verificationDetails,
  });
  return result.data;
}

/**
 * Validates and derives canonical cityKey and regionKey from property data
 * against the central Cameroon geographic registry.
 */
export function deriveCityAndRegionKeys(property: {
  city?: string;
  region?: string;
  location?: string;
  cityKey?: string;
  regionKey?: string;
}): { cityKey: string; regionKey: string; cityName: string; regionName: string } {
  // If already validly set and present in registry
  if (property.cityKey && property.regionKey) {
    const matchedTown = ALL_CAMEROON_TOWNS.find(
      (t) => t.slug === property.cityKey || slugify(t.name) === property.cityKey
    );
    if (matchedTown) {
      return {
        cityKey: matchedTown.slug,
        regionKey: slugify(matchedTown.region),
        cityName: matchedTown.name,
        regionName: matchedTown.region,
      };
    }
  }

  const rawCity = (property.city || '').trim().toLowerCase();
  const rawLocation = (property.location || '').trim().toLowerCase();
  const rawRegion = (property.region || '').trim().toLowerCase();

  // 1. Try trusted city match
  const trusted = getTrustedCity(rawCity) || getTrustedCity(rawLocation);
  if (trusted) {
    return {
      cityKey: trusted.slug,
      regionKey: slugify(trusted.region),
      cityName: trusted.name,
      regionName: trusted.region,
    };
  }

  // 2. Try match against ALL_CAMEROON_TOWNS
  const foundTown = ALL_CAMEROON_TOWNS.find((t) => {
    const tName = t.name.toLowerCase();
    const tSlug = t.slug.toLowerCase();
    return (
      rawCity === tName ||
      rawCity === tSlug ||
      rawLocation.includes(tName) ||
      t.popularQuarters.some((q) => rawLocation.includes(q.toLowerCase()))
    );
  });

  if (foundTown) {
    return {
      cityKey: foundTown.slug,
      regionKey: slugify(foundTown.region),
      cityName: foundTown.name,
      regionName: foundTown.region,
    };
  }

  // 3. Match region if town not specifically found
  const foundRegion = CAMEROON_REGIONS.find((r) => {
    const rName = r.name.toLowerCase();
    return rawRegion.includes(rName) || rawLocation.includes(rName);
  });

  const resolvedRegionName = foundRegion ? foundRegion.name : (property.region || 'Centre');
  const resolvedRegionKey = slugify(resolvedRegionName);
  const resolvedCityName = property.city || (foundRegion ? foundRegion.capital : 'Yaoundé');
  const resolvedCityKey = slugify(resolvedCityName);

  return {
    cityKey: resolvedCityKey,
    regionKey: resolvedRegionKey,
    cityName: resolvedCityName,
    regionName: resolvedRegionName,
  };
}

/**
 * Statistics adjustment helper.
 * NOTE: Real estate listing counts are maintained server-side by the onPropertyStatusWritten
 * Firestore trigger to preserve consistency and avoid client permission restrictions.
 */
export async function adjustStatistics(
  _cityKey: string,
  _cityName: string,
  _regionKey: string,
  _regionName: string,
  _delta: 1 | -1
): Promise<{ cityTotal: number; regionTotal: number }> {
  return { cityTotal: 0, regionTotal: 0 };
}

export interface RenewalResult {
  success: boolean;
  propertyId: string;
  status: 'active';
  publishedAt: string;
  renewedAt: string;
  expiresAt: string;
  updatedAt: string;
  cityKey: string;
  regionKey: string;
  slug: string;
  canonicalUrl: string;
  cityStatsTotal?: number;
  regionStatsTotal?: number;
}

/**
 * Trusted backend renewal handler:
 * - Verifies caller ownership or Admin authorization
 * - Preserves original publishedAt, slug, canonical URL, images, views
 * - Sets status = 'active'
 * - Sets renewedAt and updatedAt to server time
 * - Sets new expiresAt using configured listing duration (60 days)
 * - Atomically restores city and region totals ONLY if not already active
 */
export async function renewListingBackend(
  propertyId: string,
  callerUid: string,
  isAdmin = false
): Promise<RenewalResult> {
  if (!callerUid && !isAdmin) {
    throw new Error('Authentication required: caller must be logged in to renew a listing.');
  }

  const propRef = doc(db, 'properties', propertyId);
  const snap = await getDoc(propRef);

  if (!snap.exists()) {
    throw new Error(`Listing ${propertyId} not found.`);
  }

  const rawData = snap.data();
  const ownerId = rawData.owner_id || rawData.ownerId;

  // STRICT AUTHORIZATION: Only the listing owner or Admin may renew
  if (!isAdmin && ownerId !== callerUid) {
    throw new Error('Unauthorized: only the authenticated owner or platform admin can renew this listing.');
  }

  const now = new Date();
  const nowIso = now.toISOString();

  // Preserve original publishedAt (or initialize if previously absent)
  const existingPublishedAt = rawData.published_at || rawData.publishedAt || rawData.created_at || nowIso;
  const publishedAt = typeof existingPublishedAt === 'string'
    ? existingPublishedAt
    : (existingPublishedAt?.toDate?.() ? existingPublishedAt.toDate().toISOString() : nowIso);

  // Calculate new server-authoritative expiresAt
  const newExpiryDate = calculateExpiryDate(now, LISTING_CONFIG.DEFAULT_EXPIRY_DAYS);
  const expiresAt = newExpiryDate.toISOString();

  // Derive and validate keys
  const { cityKey, regionKey, cityName, regionName } = deriveCityAndRegionKeys({
    city: rawData.city,
    region: rawData.region,
    location: rawData.location,
    cityKey: rawData.city_key || rawData.cityKey,
    regionKey: rawData.region_key || rawData.regionKey,
  });

  const prevStatus = (rawData.status || '').toLowerCase().trim();
  const wasAlreadyActive = prevStatus === 'active' || prevStatus === 'published';

  const updatePayload: Record<string, any> = {
    status: 'active',
    published_at: publishedAt,
    publishedAt: publishedAt,
    renewed_at: nowIso,
    renewedAt: nowIso,
    expires_at: expiresAt,
    expiresAt: expiresAt,
    updated_at: nowIso,
    updatedAt: nowIso,
    city_key: cityKey,
    cityKey: cityKey,
    region_key: regionKey,
    regionKey: regionKey,
    expiry_warnings_sent: [],
    expiryWarningsSent: [],
    expiration_reminder_sent: false,
    expiration_reminder_cycle: null,
  };

  await updateDoc(propRef, updatePayload);

  // Atomically increment city & region totals only if not already active
  let statsTotals: { cityTotal: number; regionTotal: number } | undefined;
  if (!wasAlreadyActive) {
    statsTotals = await adjustStatistics(cityKey, cityName, regionKey, regionName, 1);
  }

  const slug = rawData.slug || rawData.canonical_slug || propertyId;
  const canonicalUrl = rawData.canonical_url || `https://cameroonlandmarket.com/en/property/${slug}`;

  return {
    success: true,
    propertyId,
    status: 'active',
    publishedAt,
    renewedAt: nowIso,
    expiresAt,
    updatedAt: nowIso,
    cityKey,
    regionKey,
    slug,
    canonicalUrl,
    cityStatsTotal: statsTotals?.cityTotal,
    regionStatsTotal: statsTotals?.regionTotal,
  };
}

/**
 * Idempotent batch expiration runner:
 * Scans all currently 'active' listings. If now >= expiresAt, marks them 'expired'
 * and decrements city & region statistics exactly once (with lower bound of 0).
 */
export async function processExpiredListingsBackend(): Promise<{
  totalScanned: number;
  expiredCount: number;
  expiredPropertyIds: string[];
}> {
  const expiredPropertyIds: string[] = [];
  try {
    const propsCol = collection(db, 'properties');
    const q = query(propsCol, where('status', '==', 'active'));
    const snap = await getDocs(q);

    for (const docSnap of snap.docs) {
      const data = docSnap.data();
      const expiresAt = data.expires_at || data.expiresAt;

      if (expiresAt && isListingExpired(expiresAt)) {
        const { cityKey, regionKey, cityName, regionName } = deriveCityAndRegionKeys({
          city: data.city,
          region: data.region,
          location: data.location,
          cityKey: data.city_key || data.cityKey,
          regionKey: data.region_key || data.regionKey,
        });

        const nowIso = new Date().toISOString();

        // Idempotent update: set to expired
        await updateDoc(doc(db, 'properties', docSnap.id), {
          status: 'expired',
          updated_at: nowIso,
          updatedAt: nowIso,
          expired_at: nowIso,
          expiredAt: nowIso,
        });

        // Decrement statistics exactly once
        await adjustStatistics(cityKey, cityName, regionKey, regionName, -1);
        expiredPropertyIds.push(docSnap.id);
      }
    }

    return {
      totalScanned: snap.docs.length,
      expiredCount: expiredPropertyIds.length,
      expiredPropertyIds,
    };
  } catch (err) {
    console.error('Error running processExpiredListingsBackend:', err);
    return {
      totalScanned: 0,
      expiredCount: 0,
      expiredPropertyIds: [],
    };
  }
}

/**
 * 1-Click Renewal via Token:
 * Allows owners to renew directly from their phone/email without typing a password.
 */
export async function renewListingWithTokenBackend(
  propertyId: string,
  token: string
): Promise<{
  success: boolean;
  propertyId: string;
  status: string;
  publishedAt: string;
  renewedAt: string;
  expiresAt: string;
  updatedAt: string;
  cityKey: string;
  regionKey: string;
  slug: string;
  canonicalUrl: string;
}> {
  if (!propertyId || !token) {
    throw new Error('Property ID and Renewal Token are required.');
  }

  const propRef = doc(db, 'properties', propertyId);
  const snap = await getDoc(propRef);

  if (!snap.exists()) {
    throw new Error(`Property ${propertyId} not found.`);
  }

  const rawData = snap.data();
  const storedToken = rawData.renewal_token || rawData.renewalToken;

  // If token is stored on property, verify exact match
  if (storedToken && storedToken !== token) {
    throw new Error('Invalid or expired renewal link. Please sign in to renew.');
  }

  const now = new Date();
  const nowIso = now.toISOString();

  // Preserve original publishedAt
  const existingPublishedAt = rawData.published_at || rawData.publishedAt || rawData.created_at || nowIso;
  const publishedAt = typeof existingPublishedAt === 'string'
    ? existingPublishedAt
    : (existingPublishedAt?.toDate?.() ? existingPublishedAt.toDate().toISOString() : nowIso);

  const expiresAt = calculateExpiryDate(now, LISTING_CONFIG.DEFAULT_EXPIRY_DAYS).toISOString();

  const { cityKey, regionKey, cityName, regionName } = deriveCityAndRegionKeys({
    city: rawData.city,
    region: rawData.region,
    location: rawData.location,
    cityKey: rawData.city_key || rawData.cityKey,
    regionKey: rawData.region_key || rawData.regionKey,
  });

  const prevStatus = (rawData.status || '').toLowerCase().trim();
  const wasAlreadyActive = prevStatus === 'active' || prevStatus === 'published';

  const updatePayload: Record<string, any> = {
    status: 'active',
    published_at: publishedAt,
    publishedAt: publishedAt,
    renewed_at: nowIso,
    renewedAt: nowIso,
    expires_at: expiresAt,
    expiresAt: expiresAt,
    updated_at: nowIso,
    updatedAt: nowIso,
    city_key: cityKey,
    cityKey: cityKey,
    region_key: regionKey,
    regionKey: regionKey,
    expiry_warnings_sent: [],
    expiryWarningsSent: [],
    expiration_reminder_sent: false,
    expiration_reminder_cycle: null,
  };

  await updateDoc(propRef, updatePayload);

  if (!wasAlreadyActive) {
    await adjustStatistics(cityKey, cityName, regionKey, regionName, 1);
  }

  const slug = rawData.slug || rawData.canonical_slug || propertyId;
  const canonicalUrl = rawData.canonical_url || `https://cameroonlandmarket.com/en/property/${slug}`;

  return {
    success: true,
    propertyId,
    status: 'active',
    publishedAt,
    renewedAt: nowIso,
    expiresAt,
    updatedAt: nowIso,
    cityKey,
    regionKey,
    slug,
    canonicalUrl,
  };
}
